import {
  AccountBalanceWallet,
  BarChartOutlined,
  ContactPage,
  Handshake,
  HowToReg,
  PaymentsOutlined,
  QueueMusic,
  ReceiptLong,
  SellOutlined,
} from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import LanguageIcon from "@mui/icons-material/Language";
import LogoutIcon from "@mui/icons-material/Logout";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import {
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import i18n from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCurrentUser } from "Auth/Hooks/useCurrentUser";
import { useSignOut } from "Auth/Hooks/useSignOut";
import {
  CurrencyCodes,
  type LanguageCodes,
  patchUsersSettings,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { languageCodeToI18n } from "Infrastructure/Utils/LanguageUtils";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useCurrency } from "Intl/Hooks/useCurrency";
import { useUnseenNotificationCountQuery } from "Notifications/Api/Queries/useUnseenNotificationCountQuery";
import { NotificationPanel } from "Notifications/Components/NotificationPanel";
import { UserAvatar } from "Shared/Avatar/UserAvatar";
import { NewProjectButton } from "Shared/Buttons/NewProjectButton";
import { ActorsIcon } from "Shared/Icons/Actors";
import { CubeIcon } from "Shared/Icons/Cube";
import { HomeIcon } from "Shared/Icons/Home";
import { ProjectsIcon } from "Shared/Icons/Projects";
import { SidebarMenuItem } from "Shared/Layouts/SidebarMenuItem";
import { Logo } from "Shared/Logo";
import { LogoCollapsed } from "Shared/LogoCollapsed";

const minDrawerWidth = 70;
const defaultDrawerWidth = 290;
const maxDrawerWidth = 290;
const snapThreshold = 180;

const DrawerWrapper = styled(Box)`
  position: relative;
  overflow: visible;
  height: 100vh;
  display: flex;
`;

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "isResizing",
})<{ width: number; isResizing: boolean }>`
  width: ${(props) => props.width}px;
  flex-shrink: 0;
  transition: ${(props) =>
    props.isResizing ? "none" : "width 0.2s ease-in-out"};

  & .MuiDrawer-paper {
    width: ${(props) => props.width}px;
    box-sizing: border-box;
    background: ${(props) => props.theme.colors.backgroundInput};
    border: none;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    will-change: width;
    transition: ${(props) =>
      props.isResizing ? "none" : "width 0.2s ease-in-out"};
    box-shadow: none;
  }

  .MuiTypography-root {
    color: ${(props) => props.theme.palette.text.primary};
  }

  .MuiListItemIcon-root {
    color: ${(props) => props.theme.palette.text.secondary};
  }
`;

const ResizeHandle = styled(Box)`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  cursor: col-resize;
  z-index: 1000;

  &:hover {
    background: ${(props) => props.theme.palette.primary.main}40;
  }

  &:active {
    background: ${(props) => props.theme.palette.primary.main}80;
  }
`;

const LogoContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isCollapsed",
})<{ $isCollapsed: boolean }>`
  padding: ${(props) =>
    props.$isCollapsed
      ? props.theme.spacing(Spacing.M)
      : props.theme.spacing(5)};
  border-bottom: 2px solid ${(props) => props.theme.colors.borderSubtle};
`;

const ToggleButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    prop !== "$drawerWidth" && prop !== "$isResizing",
})<{
  $drawerWidth: number;
  $isResizing: boolean;
}>`
  position: fixed;
  left: ${(props) => props.$drawerWidth - 11}px;
  top: ${(props) => props.theme.spacing(6)};
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  padding: 0;
  background-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.contrastText};
  border-radius: 50%;
  z-index: 1300;
  transition: left 0.2s ease-in-out, opacity 0.2s ease-in-out;
  opacity: ${(props) => (props.$isResizing ? 0 : 1)};
  pointer-events: ${(props) => (props.$isResizing ? "none" : "auto")};

  &:hover {
    background-color: ${(props) => props.theme.palette.primary.dark};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const UserInfoSection = styled(Box)`
  padding: ${(props) => props.theme.spacing(Spacing.M)};
  border-top: 2px solid ${(props) => props.theme.colors.borderSubtle};
  margin-top: auto;
`;

const NotificationBellSection = styled(Box)`
  padding: ${(props) => props.theme.spacing(Spacing.S, Spacing.M)};
  border-top: 2px solid ${(props) => props.theme.colors.borderSubtle};
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(Spacing.S)};
`;

const UserInfoButton = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: ${(props) => props.theme.spacing(Spacing.XS)};
  border-radius: ${(props) => props.theme.spacing(Spacing.S)};
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${(props) => props.theme.palette.background.paper};
  }
`;

export const Sidebar = (): React.ReactNode => {
  const { t } = useResource();

  const theme = useTheme();

  const [drawerWidth, setDrawerWidth] = useState(defaultDrawerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const [currencyAnchorEl, setCurrencyAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(defaultDrawerWidth);
  const rafRef = useRef<number | null>(null);

  const { data: unseenCountData } = useUnseenNotificationCountQuery();
  const unseenCount = unseenCountData?.count ?? 0;

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setLangAnchorEl(null);
    setCurrencyAnchorEl(null);
  }, []);

  const handleLanguageMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setLangAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleLanguageMenuClose = useCallback(() => {
    setLangAnchorEl(null);
  }, []);

  const handleLanguageChange = useCallback(
    async (language: LanguageCodes) => {
      i18n.changeLanguage(languageCodeToI18n(language));
      handleMenuClose();
      try {
        await patchUsersSettings({ language });
      } catch {
        // Silently ignore — UI already updated via i18n, localStorage persists the preference
      }
    },
    [handleMenuClose],
  );

  const languageOptions = useMemo(
    () =>
      [
        {
          code: "Czech" as LanguageCodes,
          label: t(Resources.Common.Languages.Czech),
        },
        {
          code: "English" as LanguageCodes,
          label: t(Resources.Common.Languages.English),
        },
        {
          code: "Deutsch" as LanguageCodes,
          label: t(Resources.Common.Languages.Deutsch),
        },
      ] as const,
    [t],
  );

  const { userCurrency, setCurrency } = useCurrency();

  const handleCurrencyMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setCurrencyAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleCurrencyMenuClose = useCallback(() => {
    setCurrencyAnchorEl(null);
  }, []);

  const handleCurrencyChange = useCallback(
    (currency: CurrencyCodes) => {
      setCurrency(currency);
      handleMenuClose();
    },
    [handleMenuClose, setCurrency],
  );

  const currencyOptions = useMemo(
    () =>
      [
        CurrencyCodes.EUR,
        CurrencyCodes.CZK,
        CurrencyCodes.GBP,
        CurrencyCodes.PLN,
        CurrencyCodes.CHF,
        CurrencyCodes.SEK,
        CurrencyCodes.NOK,
        CurrencyCodes.DKK,
        CurrencyCodes.HUF,
        CurrencyCodes.RON,
      ].map((code) => ({
        code,
        label: t(Resources.Common.Currencies[code]),
      })),
    [t],
  );

  const _currentLanguageLabel = useMemo(() => {
    const match = languageOptions.find(
      (opt) => languageCodeToI18n(opt.code) === i18n.language,
    );
    return match?.label ?? "";
  }, [languageOptions]);

  const toggleSidebar = useCallback(() => {
    setDrawerWidth((prevWidth) =>
      prevWidth === minDrawerWidth ? defaultDrawerWidth : minDrawerWidth,
    );
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = drawerWidth;
      e.preventDefault();
    },
    [drawerWidth],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      // Cancel any pending animation frame
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }

      // Use requestAnimationFrame for smooth updates
      rafRef.current = requestAnimationFrame(() => {
        const delta = e.clientX - startXRef.current;
        const newWidth = startWidthRef.current + delta;

        if (newWidth >= minDrawerWidth && newWidth <= maxDrawerWidth) {
          setDrawerWidth(newWidth);
        }
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      // Auto-snap to minimum width if below threshold
      if (drawerWidth < snapThreshold) {
        setDrawerWidth(minDrawerWidth);
      } else {
        setDrawerWidth(defaultDrawerWidth);
      }
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isResizing, drawerWidth]);

  const { user, isSysAdmin: isAdmin, isRootPartner } = useCurrentUser();

  const userLogin = user?.login ?? "";
  const firstName = user?.firstName ?? "";
  const lastName = user?.lastName ?? "";

  const menuItems = useMemo(
    () => [
      {
        text: t(Resources.Common.Dashboard),
        icon: <HomeIcon />,
        to: AppRouting.Dashboard.path(),
      },
      ...(isAdmin || isRootPartner
        ? [
            {
              text: t(Resources.Common.Partners),
              icon: <Handshake />,
              to: AppRouting.Partners.path(),
            },
          ]
        : []),
      ...(!isAdmin
        ? [
            {
              text: t(Resources.Common.Clients),
              icon: <ContactPage />,
              to: AppRouting.Clients.path(),
            },
            {
              text: t(Resources.Common.Projects),
              icon: <ProjectsIcon />,
              to: AppRouting.Projects.path(),
            },
            {
              text: t(Resources.Common.Actors),
              icon: <ActorsIcon />,
              to: AppRouting.Actors.path(),
            },
            {
              text: t(Resources.Common.MusicAudio),
              icon: <QueueMusic />,
              to: AppRouting.MusicAudio.path(),
            },
            {
              text: t(Resources.Common.Wallet),
              icon: <AccountBalanceWallet />,
              to: AppRouting.Billing.path(),
            },
            {
              text: t(Resources.Common.Invoices),
              icon: <ReceiptLong />,
              to: AppRouting.PartnerInvoices.path(),
            },
          ]
        : []),
      ...(isAdmin
        ? [
            {
              text: t(Resources.Common.Modules),
              icon: <CubeIcon />,
              to: AppRouting.Modules.path(),
            },
            {
              text: t(Resources.Common.PriceCategories),
              icon: <SellOutlined />,
              to: AppRouting.PriceCategories.path(),
            },
            {
              text: t(Resources.Common.Actors),
              icon: <ActorsIcon />,
              to: AppRouting.Actors.path(),
            },
            {
              text: t(Resources.Common.RegistrationRequests),
              icon: <HowToReg />,
              to: AppRouting.RegistrationRequests.path(),
            },
            {
              text: t(Resources.Common.WithdrawalApprovals),
              icon: <PaymentsOutlined />,
              to: AppRouting.WithdrawalApprovals.path(),
            },
            {
              text: t(Resources.Common.Invoices),
              icon: <ReceiptLong />,
              to: AppRouting.Invoices.path(),
            },
            {
              text: t(Resources.Common.ActivityLog),
              icon: <HistoryIcon />,
              to: AppRouting.ActivityLog.path(),
            },
            {
              text: t(Resources.Common.Reporting),
              icon: <BarChartOutlined />,
              to: AppRouting.Reporting.path(),
            },
          ]
        : []),
    ],
    [t, isAdmin, isRootPartner],
  );
  const signOut = useSignOut();
  const isCollapsed = useMemo(() => drawerWidth <= 140, [drawerWidth]);

  return (
    <DrawerWrapper>
      <StyledDrawer
        variant="permanent"
        width={drawerWidth}
        isResizing={isResizing}
      >
        <ResizeHandle onMouseDown={handleMouseDown} />
        <LogoContainer $isCollapsed={isCollapsed}>
          {isCollapsed ? <LogoCollapsed /> : <Logo size="sm" />}
        </LogoContainer>
        <Box
          sx={{
            overflow: "auto",
            paddingTop: Spacing.S,
            flexGrow: 1,
            width: "100%",
          }}
        >
          <Box sx={{ padding: theme.spacing(Spacing.M, Spacing.S) }}>
            <NewProjectButton isCollapsed={isCollapsed} />
          </Box>
          <List sx={{ width: "100%", overflow: "hidden", padding: 2 }}>
            {menuItems.map((item) => (
              <SidebarMenuItem
                key={item.text}
                text={item.text}
                icon={item.icon}
                to={item.to}
                isCollapsed={isCollapsed}
              />
            ))}
          </List>
        </Box>
        <NotificationBellSection
          sx={{ justifyContent: isCollapsed ? "center" : "flex-start" }}
        >
          <IconButton
            aria-label="Notifications"
            onClick={() => setNotificationPanelOpen(true)}
            sx={{ color: "text.secondary" }}
          >
            <Badge
              badgeContent={unseenCount > 99 ? "99+" : unseenCount}
              color="error"
              invisible={unseenCount === 0}
            >
              <NotificationsNoneIcon />
            </Badge>
          </IconButton>
          {!isCollapsed && (
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
              sx={{ cursor: "pointer" }}
              onClick={() => setNotificationPanelOpen(true)}
            >
              {t(Resources.Notifications.Title)}
            </Typography>
          )}
        </NotificationBellSection>
        {!isCollapsed && (
          <UserInfoSection>
            <UserInfoButton onClick={handleMenuOpen}>
              <Stack direction="row" spacing={Spacing.S} alignItems="center">
                <UserAvatar firstName={firstName} lastName={lastName} />
                <Stack spacing={0}>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    fontWeight={500}
                  >
                    {firstName} {lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userLogin}
                  </Typography>
                </Stack>
              </Stack>
              <IconButton size="small" sx={{ color: "text.secondary" }}>
                {anchorEl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </UserInfoButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "background.paper",
                    width: anchorEl ? anchorEl.offsetWidth : "auto",
                    boxShadow: "none",
                  },
                },
              }}
            >
              <MenuItem onClick={handleLanguageMenuOpen}>
                <ListItemIcon>
                  <LanguageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t(Resources.Common.Language)}</ListItemText>
                <NavigateNextIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", ml: 1 }}
                />
              </MenuItem>
              <MenuItem onClick={handleCurrencyMenuOpen}>
                <ListItemIcon>
                  <CurrencyExchangeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t(Resources.Common.Currency)}</ListItemText>
                <NavigateNextIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", ml: 1 }}
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  signOut();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t(Resources.Common.SignOut)}</ListItemText>
              </MenuItem>
            </Menu>
            <Menu
              anchorEl={langAnchorEl}
              open={Boolean(langAnchorEl)}
              onClose={handleLanguageMenuClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "background.paper",
                    boxShadow: "none",
                    ml: 0.5,
                  },
                },
              }}
            >
              {languageOptions.map((opt) => (
                <MenuItem
                  key={opt.code}
                  onClick={() => handleLanguageChange(opt.code)}
                  selected={languageCodeToI18n(opt.code) === i18n.language}
                >
                  <ListItemText>{opt.label}</ListItemText>
                  {languageCodeToI18n(opt.code) === i18n.language && (
                    <CheckIcon
                      fontSize="small"
                      sx={{ ml: 1, color: "primary.main" }}
                    />
                  )}
                </MenuItem>
              ))}
            </Menu>
            <Menu
              anchorEl={currencyAnchorEl}
              open={Boolean(currencyAnchorEl)}
              onClose={handleCurrencyMenuClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "background.paper",
                    boxShadow: "none",
                    ml: 0.5,
                    maxHeight: 400,
                  },
                },
              }}
            >
              {currencyOptions.map((opt) => (
                <MenuItem
                  key={opt.code}
                  onClick={() => handleCurrencyChange(opt.code)}
                  selected={opt.code === userCurrency}
                >
                  <ListItemText>{opt.label}</ListItemText>
                  {opt.code === userCurrency && (
                    <CheckIcon
                      fontSize="small"
                      sx={{ ml: 1, color: "primary.main" }}
                    />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </UserInfoSection>
        )}
        {isCollapsed && (
          <UserInfoSection>
            <Stack spacing={Spacing.S} alignItems="center">
              <UserAvatar firstName={firstName} lastName={lastName} />
              <Button
                variant="outlined"
                onClick={() => signOut()}
                sx={{
                  borderColor: "divider",
                  color: "text.primary",
                  minWidth: 0,
                  padding: Spacing.S,
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                <LogoutIcon />
              </Button>
            </Stack>
          </UserInfoSection>
        )}
      </StyledDrawer>
      <ToggleButton
        $drawerWidth={drawerWidth}
        $isResizing={isResizing}
        onClick={toggleSidebar}
      >
        {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </ToggleButton>
      <NotificationPanel
        isOpen={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
      />
    </DrawerWrapper>
  );
};
